import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import get from 'lodash.get';
import { Grid, Button, IconButton, Chip } from '@material-ui/core';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { FormattedMessage } from 'react-intl';

import { GUIDE_SUB_TYPE, GUIDE_TYPE } from 'yeself-common/ModelConstant';

import ConfirmDialog from 'Console/components/common/confirmDialog/ConfirmDialog';

import { guideQueryByType } from 'Console/utils/guideUtils';
import { MiUiTheme } from 'Console/utils/styleUtils';

import GuideListMenuItem from './GuideListItemMenu';

const styles = (theme) => ({
    chipsBox: {
        textTransform: 'uppercase',
        display: 'flex',
        marginLeft: theme.typography.pxToRem(10),
        marginRight: theme.typography.pxToRem(10),
        border: 'none',
    },
    chip: {
        height: theme.typography.pxToRem(22),
        fontSize: theme.typography.pxToRem(9),
        marginRight: theme.typography.pxToRem(10),
        fontWeight: 600,
        letterSpacing: '0.0357143em',
    },
    guideFooter: {
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        paddingTop: theme.typography.pxToRem(10),
        marginTop: theme.typography.pxToRem(5),
        paddingRight: 0,
        width: 'auto',
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
    },
    buttons: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        [theme.breakpoints.down('md')]: {
            width: 'auto',
        },
    },
    step: {
        extend: 'item',
        marginLeft: theme.typography.pxToRem(10),
    },

    itemsBox: {
        width: 'auto',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    publishForm: {
        marginTop: theme.typography.pxToRem(10),
        marginBottom: theme.typography.pxToRem(35),
    },
    tab: {
        paddingRight: theme.typography.pxToRem(30),
        paddingLeft: theme.typography.pxToRem(30),
        [theme.breakpoints.between('xs', 'md')]: {
            paddingRight: theme.typography.pxToRem(0),
            paddingLeft: theme.typography.pxToRem(0),
        },
    },
});

class GuideFooter extends Component {
    windowOpen = (guide) => () => {
        const {
            form: { getFieldValue },
        } = this.props;
        const locale = getFieldValue('locale') || 'en';
        const urlSymbol = (url) => (/\?/.test(url) ? '&' : '?');
        const language = locale.code || 'en';

        const [head, tail] = guide.initialUrl.split('#');
        const symbol = urlSymbol(head);
        const hashString = tail ? `#${tail}` : '';
        const guideQueryParam = guideQueryByType(guide.type);
        const updatedUrl = `${head}${symbol}${guideQueryParam}=${guide.id}&ye-lang=${language}${hashString}`;

        window.open(updatedUrl, `${guideQueryParam}=${guide.id}&ye-lang=${language}`);
    };

    render() {
        const {
            classes,
            editGuide,
            showDeleteDialog,
            intl,
            guide,
            showActionsMenu,
            onDuplicateGuide,
            handleUpdateState,
            handleUpdateGuide,
            form,
            menuAnchorEl,
            closeActionsMenu,
            removeGuide,
        } = this.props;
        const sandbox = get(guide, 'graph.metadata.sandbox');
        const journeyTracker = get(guide, 'graph.metadata.journeyTracker.enabled');
        const issues = get(guide, 'issues') || [];
        const formErrors = Object.values(form.getFieldsError()).filter((error) => error);
        const formValues = Object.values(form.getFieldsValue()).filter((value) => value);

        const cancelEditGuide = () => {
            handleUpdateState({ editGuide: false, showGuideDetail: false, tabOpened: 0 })();
            form.resetFields();
        };

        const displayChipLabel = () => {
            const { guide } = this.props;
            switch (guide.type) {
                case GUIDE_TYPE.TOUR:
                    return guide.subType === GUIDE_SUB_TYPE.PLAYLIST ? (
                        <FormattedMessage id="Guides.menuItem.playlist" defaultMessage="Playlist" />
                    ) : (
                        <FormattedMessage id="Guides.menuItem.tour" defaultMessage="Tour" />
                    );
                case GUIDE_TYPE.INTERACTION_COLLECTOR:
                    return (
                        <FormattedMessage
                            id="Guides.menuItem.interaction.collector"
                            defaultMessage="Interaction collector"
                        />
                    );
                case GUIDE_TYPE.ERROR_COLLECTOR:
                    return (
                        <FormattedMessage
                            id="Guides.menuItem.error.collector"
                            defaultMessage="Error Collector"
                        />
                    );
                case GUIDE_TYPE.APPLICATION_AUGMENTATION:
                    return (
                        <FormattedMessage
                            id="Guides.menuItem.application.augmentation"
                            defaultMessage="Tooltips"
                        />
                    );
                case GUIDE_TYPE.RECORDING:
                    return <FormattedMessage id="Guides.menuItem.recording" defaultMessage="Capture" />;
                default:
                    return '';
            }
        };

        return (
            <Grid id="yeself-guide-footer-wrapper" className={classes.guideFooter}>
                <Grid className={classes.itemsBox}>
                    <Grid id="yeself-chips-box" className={classes.chipsBox}>
                        <Chip
                            id="yeself-chip"
                            className={classes.chip}
                            label={displayChipLabel()}
                            color="secondary"
                        />
                        {get(guide, 'graph.metadata.contextHelp.enabled') && (
                            <Chip
                                id="yeself-chip"
                                className={classes.chip}
                                label={<FormattedMessage id="Guides.context" defaultMessage="Context" />}
                                color="secondary"
                            />
                        )}
                        {sandbox && (
                            <Chip
                                id="yeself-chip"
                                className={classes.chip}
                                label={
                                    <FormattedMessage
                                        id="SettingsGeneral.field.unpublished"
                                        defaultMessage="Unpublished"
                                    />
                                }
                                color="secondary"
                            />
                        )}

                        {journeyTracker && (
                            <Chip
                                id="yeself-chip"
                                className={classes.chip}
                                label={<FormattedMessage id="Guides.journey" defaultMessage="Journey" />}
                                color="secondary"
                            />
                        )}

                        {issues.map((issue) => (
                            <Chip
                                id="yeself-chip"
                                key={issue.id}
                                className={classes.chip}
                                label={issue.label}
                                style={MiUiTheme.palette.tertiary}
                            />
                        ))}
                    </Grid>
                </Grid>

                {!editGuide && (
                    <Grid id="yeself-guide-footer-buttons" className={classes.buttons}>
                        <div>
                            <IconButton
                                aria-label="More"
                                aria-owns={showActionsMenu ? 'long-menu' : undefined}
                                style={{ color: '#282828' }}
                                aria-haspopup="true"
                                onClick={(event) =>
                                    handleUpdateState({
                                        showActionsMenu: true,
                                        menuAnchorEl: event.currentTarget,
                                    })()
                                }
                            >
                                <MoreHorizIcon />
                            </IconButton>

                            <GuideListMenuItem
                                layerId={guide.id}
                                guide={guide}
                                isOpen={showActionsMenu}
                                handleUpdateState={handleUpdateState}
                                anchorEl={menuAnchorEl}
                                onClose={closeActionsMenu}
                                onDuplicateGuide={onDuplicateGuide}
                            />
                        </div>
                        <Button
                            id="yeself-delete-button"
                            style={{ color: '#EC6054' }}
                            className={classes.item}
                            onClick={handleUpdateState({ showDeleteDialog: true })}
                        >
                            {intl.formatMessage({ id: 'Buttons.delete' })}
                        </Button>

                        <Button
                            id="yeself-edit-button"
                            onClick={handleUpdateState({ editGuide: true, showPublishOptions: false })}
                            style={{ color: '#282828' }}
                            className={classes.item}
                        >
                            {intl.formatMessage({ id: 'Buttons.edit' })}
                        </Button>
                        {guide.type !== GUIDE_TYPE.RECORDING && guide.initialUrl?.length > 0 && (
                            <Button
                                id="yeself-open-in-editor-button"
                                color="primary"
                                className={classes.item}
                                onClick={this.windowOpen(guide)}
                            >
                                {intl.formatMessage({ id: 'Buttons.openInEditor' })}
                            </Button>
                        )}
                    </Grid>
                )}

                {editGuide && (
                    <Grid className={classes.buttonsWrapper} container justify="flex-end">
                        <Button
                            id="yeself-cancel-button"
                            style={{ color: 'rgba(0, 0, 0, 0.38)', marginRight: '19px' }}
                            onClick={cancelEditGuide}
                        >
                            {intl.formatMessage({
                                id: 'Buttons.cancel',
                            })}
                        </Button>

                        <Button
                            id="yeself-button"
                            disabled={formErrors.length > 0 || formValues.length < 2}
                            variant="contained"
                            color="primary"
                            onClick={handleUpdateGuide}
                            className="yeself-guide-footer-save"
                        >
                            {intl.formatMessage({
                                id: 'Buttons.save',
                            })}
                        </Button>
                    </Grid>
                )}

                <ConfirmDialog
                    title={intl.formatMessage({ id: 'Guides.delete.message' })}
                    open={showDeleteDialog}
                    content=""
                    closeAction={handleUpdateState({ showDeleteDialog: false })}
                    action={removeGuide}
                    agreeLabel={intl.formatMessage({ id: 'Buttons.delete' })}
                    disagreeLabel={intl.formatMessage({ id: 'Buttons.cancel' })}
                />
            </Grid>
        );
    }
}

export default withStyles(styles)(GuideFooter);
